'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import NextImage, { type ImageProps } from 'next/image';
import {
  getStrapiMediaUrl,
  getStrapiOriginalMediaUrl,
  isStrapiVideoMedia,
  isVideoUrl,
  type StrapiFlatMedia,
} from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { cn } from '@/utils/cn';
import { useMediaInViewport } from '@/hooks/useMediaInViewport';

/**
 * Drop-in replacement for `next/image` that always shows a local brand
 * placeholder underneath until the real image/video is ready (or failed).
 */
interface CmsImageProps extends Omit<ImageProps, 'src' | 'onError' | 'onLoad'> {
  src: ImageProps['src'] | null | undefined;
  media?: StrapiFlatMedia | null;
  posterSrc?: string | null;
  posterMedia?: StrapiFlatMedia | null;
  videoClassName?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  fallbackSrc?: string;
  /** Detach video URLs until the media enters the viewport. Defaults to true unless priority. */
  deferVideoLoad?: boolean;
  alt: string;
}

const isUsableSrc = (src: CmsImageProps['src']): src is ImageProps['src'] => {
  if (src == null) return false;
  if (typeof src === 'string') return src.trim().length > 0;
  return true;
};

export default function CmsImage({
  src,
  media,
  posterSrc,
  posterMedia,
  videoClassName,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  fallbackSrc = MEDIA_PLACEHOLDER,
  deferVideoLoad,
  alt,
  className,
  fill,
  width,
  height,
  sizes,
  priority,
  style,
  ...rest
}: CmsImageProps) {
  const stringSrc = typeof src === 'string' ? src : null;
  const videoSrc =
    (isStrapiVideoMedia(media) ? getStrapiOriginalMediaUrl(media) : null) ||
    (isVideoUrl(stringSrc) ? stringSrc : null);
  const resolvedPoster =
    posterSrc || getStrapiMediaUrl(posterMedia, 'large') || (!videoSrc ? null : fallbackSrc);
  const resolvedMediaImage = !videoSrc && media ? getStrapiMediaUrl(media, 'large') : null;
  const initialSrc: ImageProps['src'] =
    resolvedMediaImage || (isUsableSrc(src) ? src : fallbackSrc);

  const [currentSrc, setCurrentSrc] = useState<ImageProps['src']>(initialSrc);
  const [isLoaded, setIsLoaded] = useState(initialSrc === fallbackSrc);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const shouldDeferVideo = deferVideoLoad ?? !priority;
  const { targetRef, shouldLoadMedia } = useMediaInViewport<HTMLSpanElement>({
    disabled: !shouldDeferVideo,
  });

  useEffect(() => {
    setCurrentSrc(initialSrc);
    setIsLoaded(initialSrc === fallbackSrc);
    setVideoReady(
      Boolean(
        videoRef.current?.readyState &&
        videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA,
      ),
    );
  }, [initialSrc, fallbackSrc, videoSrc]);

  const wrapperClass = fill
    ? 'absolute inset-0 block overflow-hidden'
    : 'relative block w-full overflow-hidden';

  const placeholderImage = (
    <NextImage
      src={fallbackSrc}
      alt=""
      aria-hidden
      fill={Boolean(fill)}
      width={fill ? undefined : typeof width === 'number' ? width : 1920}
      height={fill ? undefined : typeof height === 'number' ? height : 1080}
      className={cn(fill ? 'object-cover' : 'h-auto w-full', className)}
      sizes={sizes || '100vw'}
      priority={priority}
      unoptimized
      style={
        fill
          ? undefined
          : {
              position: isLoaded && currentSrc !== fallbackSrc ? 'absolute' : 'relative',
              inset: 0,
              width: '100%',
              height: 'auto',
            }
      }
    />
  );

  if (videoSrc) {
    const videoStyle: CSSProperties = fill
      ? { ...style, position: 'absolute', inset: 0, width: '100%', height: '100%' }
      : { ...style, width: '100%', height: 'auto' };
    const poster = resolvedPoster || fallbackSrc;

    return (
      <span ref={targetRef} className={wrapperClass}>
        {!videoReady ? placeholderImage : null}
        <video
          ref={videoRef}
          src={shouldLoadMedia ? videoSrc : undefined}
          aria-label={alt || undefined}
          className={cn(
            'pointer-events-none transition-opacity duration-300',
            videoClassName || className,
            videoReady ? 'opacity-100' : 'opacity-0',
            fill ? 'absolute inset-0 h-full w-full object-cover' : 'h-auto w-full',
          )}
          autoPlay={shouldLoadMedia && autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          controls={false}
          disablePictureInPicture
          preload={shouldLoadMedia ? (priority || !autoPlay ? 'auto' : 'metadata') : 'none'}
          poster={poster}
          width={!fill ? (typeof width === 'number' ? width : undefined) : undefined}
          height={!fill ? (typeof height === 'number' ? height : undefined) : undefined}
          style={
            fill
              ? videoStyle
              : {
                  ...videoStyle,
                  position: videoReady ? 'relative' : 'absolute',
                  inset: 0,
                }
          }
          onLoadedData={(event) => {
            const video = event.currentTarget;
            if (!autoPlay) {
              video.pause();
              // Nudge past 0 so browsers paint a visible still frame.
              if (video.currentTime < 0.05) {
                try {
                  video.currentTime = 0.1;
                } catch {
                  // Ignore seek errors on some mobile browsers.
                }
              }
            }
            setVideoReady(true);
          }}
          onCanPlay={() => setVideoReady(true)}
          onPlaying={() => setVideoReady(true)}
          onError={() => setVideoReady(false)}
        />
      </span>
    );
  }

  const showingFallback = currentSrc === fallbackSrc;

  return (
    <span className={wrapperClass}>
      {/* Always keep placeholder visible until the real asset has loaded */}
      {!isLoaded || showingFallback ? placeholderImage : null}

      {!showingFallback ? (
        <NextImage
          {...rest}
          src={currentSrc}
          alt={alt}
          className={cn(
            fill ? 'object-cover' : undefined,
            className,
            'transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0',
          )}
          fill={Boolean(fill)}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          sizes={sizes}
          priority={priority}
          style={{
            ...style,
            ...(fill
              ? undefined
              : !isLoaded
                ? { position: 'absolute', inset: 0, width: '100%', height: '100%' }
                : undefined),
          }}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            if (currentSrc !== fallbackSrc) {
              setCurrentSrc(fallbackSrc);
              setIsLoaded(true);
            }
          }}
        />
      ) : null}
    </span>
  );
}
